/**
 * SettingsDialog
 *
 * General settings modal with tabs (e.g. Audio for WebRTC).
 * Opened from the profile menu "Settings" item.
 */

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import CloseIcon from '@mui/icons-material/Close';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import TuneIcon from '@mui/icons-material/Tune';
import { useState } from 'react';

import { AudioSettingsTab } from '@/modules/webrtc/presentation/AudioSettingsTab';
import { CallConfigTab } from '@/modules/users/presentation/components/CallConfigTab';

// ============================================================================
// Types
// ============================================================================

export interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

type SettingsTabValue = 'audio' | 'call_config';

// ============================================================================
// Component
// ============================================================================

export function SettingsDialog({ open, onClose }: SettingsDialogProps) {
  const [tab, setTab] = useState<SettingsTabValue>('call_config');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Settings
        <IconButton aria-label="Close settings" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(_, value: SettingsTabValue) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tab value="call_config" label="Call Config" icon={<TuneIcon />} iconPosition="start" />
        <Tab value="audio" label="Audio" icon={<HeadphonesIcon />} iconPosition="start" />
      </Tabs>
      <DialogContent sx={{ pt: 2 }}>
        {tab === 'audio' && <AudioSettingsTab />}
        {tab === 'call_config' && <CallConfigTab onSaved={onClose} />}
      </DialogContent>
    </Dialog>
  );
}
